/******************************************************************************
 * FILE:
 * src/modules/expenseManagement/workflow/hooks/useWorkflowStep.js
 *
 * MODULE:
 * Expense Management
 *
 * FEATURE:
 * Workflow Step
 *
 * PURPOSE:
 * React Hook for Workflow Step Management
 *
 * RESPONSIBILITY:
 * • Load Workflow Steps
 * • Load Workflow Step Details
 * • Create Workflow Step
 * • Update Workflow Step
 * • Delete Workflow Step
 * • Move Step Up
 * • Move Step Down
 * • Activate Workflow Step
 * • Deactivate Workflow Step
 *
 * ARCHITECTURE:
 * • No UI
 * • No JSX
 * • Uses workflowStepService
 ******************************************************************************/

import {
  useCallback,
  useState,
} from "react";

import workflowStepService
from "../services/workflowStepService";

export default function useWorkflowStep() {

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [workflowSteps, setWorkflowSteps] =
    useState([]);

  const [workflowStep, setWorkflowStep] =
    useState(null);

  /**
   * ------------------------------------------------------------------------
   * LOAD WORKFLOW STEPS
   * ------------------------------------------------------------------------
   */
  const loadWorkflowSteps =
    useCallback(
      async (
        workflowId,
        params = {}
      ) => {

        try {

          setLoading(true);

          setError(null);

          const data =
            await workflowStepService
              .getWorkflowSteps(
                workflowId,
                params
              );

          setWorkflowSteps(data);

          return data;

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to load workflow steps.";

          setError(message);

          throw err;

        } finally {

          setLoading(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * LOAD WORKFLOW STEP
   * ------------------------------------------------------------------------
   */
  const loadWorkflowStep =
    useCallback(
      async (
        workflowStepId
      ) => {

        try {

          setLoading(true);

          setError(null);

          const data =
            await workflowStepService
              .getWorkflowStep(
                workflowStepId
              );

          setWorkflowStep(data);

          return data;

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to load workflow step.";

          setError(message);

          throw err;

        } finally {

          setLoading(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * CREATE
   * ------------------------------------------------------------------------
   */
  const createWorkflowStep =
    useCallback(
      async (
        payload
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowStepService
            .createWorkflowStep(
              payload
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to create workflow step.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * UPDATE
   * ------------------------------------------------------------------------
   */
  const updateWorkflowStep =
    useCallback(
      async (
        workflowStepId,
        payload
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowStepService
            .updateWorkflowStep(
              workflowStepId,
              payload
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to update workflow step.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * DELETE
   * ------------------------------------------------------------------------
   */
  const deleteWorkflowStep =
    useCallback(
      async (
        workflowStepId
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowStepService
            .deleteWorkflowStep(
              workflowStepId
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to delete workflow step.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * MOVE UP
   * ------------------------------------------------------------------------
   */
  const moveWorkflowStepUp =
    useCallback(
      async (
        workflowStepId
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowStepService
            .moveWorkflowStepUp(
              workflowStepId
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to move workflow step.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * MOVE DOWN
   * ------------------------------------------------------------------------
   */
  const moveWorkflowStepDown =
    useCallback(
      async (
        workflowStepId
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowStepService
            .moveWorkflowStepDown(
              workflowStepId
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to move workflow step.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * ACTIVATE
   * ------------------------------------------------------------------------
   */
  const activateWorkflowStep =
    useCallback(
      async (
        workflowStepId
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowStepService
            .activateWorkflowStep(
              workflowStepId
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to activate workflow step.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * DEACTIVATE
   * ------------------------------------------------------------------------
   */
  const deactivateWorkflowStep =
    useCallback(
      async (
        workflowStepId
      ) => {

        try {

          setSaving(true);

          setError(null);

          return await workflowStepService
            .deactivateWorkflowStep(
              workflowStepId
            );

        } catch (err) {

          const message =
            err?.response?.data?.message
            || err.message
            || "Failed to deactivate workflow step.";

          setError(message);

          throw err;

        } finally {

          setSaving(false);
        }

      },
      []
    );

  /**
   * ------------------------------------------------------------------------
   * RETURN
   * ------------------------------------------------------------------------
   */
  return {

    loading,

    saving,

    error,

    workflowSteps,

    workflowStep,

    loadWorkflowSteps,

    loadWorkflowStep,

    createWorkflowStep,

    updateWorkflowStep,

    deleteWorkflowStep,

    moveWorkflowStepUp,

    moveWorkflowStepDown,

    activateWorkflowStep,

    deactivateWorkflowStep,

  };
}